<template>
    <div>
        <div class="paygrid-table" v-if="paygridData">
            <portal-target name="actionsPortal"></portal-target>

            <info-widget v-for="company in paygridData" :key="company.id" :collapsible="true" :is-expanded="true">
                <template v-slot:title><span class="text-muted">Company: </span> {{ company.name }}</template>
                <slot>
                    <PGFastTable
                        class="p-2"
                        :recordLabel="recordLabel"
                        :sections="company.sectionLabels"
                        :headers="headers"
                        :data="company.tableData"
                        :showSearch="true"
                        :showFilter="true"
                        :showMoreActions="false"
                        :bottomOffset="bottomOffset"
                        @click="onClick"
                        :isRowClickable="false"
                        @blur="onBlur"
                        actionsPortalTo="actionsPortal"
                        >
                        <template #section-status="{ source }">
                            <section-status-badges
                                v-if="source.sectionStatus"
                                :status="source.sectionStatus"
                                :obj-id="source.objId"
                                @reopen="onSectionReopen"
                            />
                        </template>
                    </PGFastTable>
                </slot>
            </info-widget>

            <employee-sidebar
                :client="client"
                :employee-id="currentEmployee"
                v-if="currentEmployee"
                @close="currentEmployee = null"
                tab="payroll"
                @updated="$emit('refresh')"
                :key="currentEmployee"/>

        </div>
    </div>

</template>

<script>
import PGFastTable from '@/components/PGFastTable'
import TableFilter from '@/components/FastTable/Filter'
import EmployeeSidebar from '@/components/EmployeeSidebar'
import InfoWidget from '@/components/InfoWidget'
import SectionStatusBadges from './SectionStatusBadges'

export default {
    components: {PGFastTable, TableFilter, EmployeeSidebar, InfoWidget, SectionStatusBadges },
    props: {
        client: Object,
        payrollRun: Object,
        mode: String,
        // Space the page reserves below the grid (the Back/Continue footer),
        // so the table sizes itself to the remaining height instead of the full viewport.
        bottomOffset: {
            type: Number, default: 0,
        },
    },
    emits: ['blur', 'click', 'open-earnings-statement', 'open-modify-earnings', 'add-separate-paycheck', 'section-reopen', 'refresh'],
    computed: {
        isSalariedMode() {
            return this.mode === 'salaried'
        },
        headers() {
            // The salaried step drops Rate of Pay and Overtime, and the hours column is
            // labeled Regular (salary earnings are always hours-based).
            if (this.isSalariedMode) {
                return [
                    {label: 'Employee', classes: 'cell-hide-subrow', isSearchable: true, type: "action"},
                    {label: 'Warnings', classes: 'cell-hide-subrow cw-warning', isSearchable: false, isFilterable: true},
                    {label: 'Position', classes: 'cw-9', isSearchable: true, isFilterable: true},
                    {label: 'Type', classes: 'cell-narrow', isSearchable: false, isFilterable: true},
                    {label: 'Regular', classes: 'cw-6', isSearchable: false, type: "decimal-input"},
                    {label: 'Amount', classes: 'cw-6', isSearchable: false, type: "currency-input"},
                ]
            }
            return [
                {label: 'Employee', classes: 'cell-hide-subrow', isSearchable: true, type: "action"},
                {label: 'Warnings', classes: 'cell-hide-subrow cw-warning', isSearchable: false, isFilterable: true},
                {label: 'Rate of Pay', classes: 'cw-8', isSearchable: false,},
                {label: 'Position', classes: 'cw-9', isSearchable: true, isFilterable: true},
                {label: 'Type', classes: 'cell-narrow', isSearchable: false, isFilterable: true},
                {label: 'Hours/Units', classes: 'cw-6', isSearchable: false, type: "decimal-input"},
                {label: 'Overtime', classes: 'cw-6', isSearchable: false, type: "decimal-input"},
                {label: 'Amount', classes: 'cw-6', isSearchable: false, type: "currency-input"},
            ]
        },
        paygridData() {
            return this.makePaygridData()
        },
        locationStatusesById() {
            const statusesById = {}
            if (this.payrollRun.location_statuses) {
                this.payrollRun.location_statuses.forEach(ls => {
                    statusesById[ls.id] = ls.status
                })
            }
            return statusesById
        }
    },
    data() {
        return {
            search: '',
            currentEmployee: null,
            recordLabel: "employees",
        }
    },
    methods: {
        makePaygridData() {
            const paygridData = []
            this.payrollRun.companies.forEach(company => {
                const companySectionLabels = []
                const companyTableData = []

                company.locations.forEach(location => {
                    const sectionData = []
                    let locationStatus = this.locationStatusesById[location.id] || null

                    location.employees.forEach((employee) => {

                        employee.paychecks.forEach((paycheck) => {
                            console.log('paycheck', paycheck)

                            let row = null

                            const positionsBySlug = {};

                            for (const earning of paycheck.earnings || []) {
                                // Each step shows only its own earnings: salaried jobs (pay
                                // types S / SNE) on the salaried step, everything else —
                                // including legacy earnings with no pay_type — on the hourly step.
                                const earningIsSalaried = earning.pay_type === 'S' || earning.pay_type === 'SNE'
                                if (earningIsSalaried !== this.isSalariedMode) {
                                    continue
                                }
                                const positionSlug = earning.position_slug;

                                if (!positionsBySlug[positionSlug]) {
                                    positionsBySlug[positionSlug] = {
                                        slug: positionSlug,
                                        earnings: []
                                    }
                                }

                                positionsBySlug[positionSlug].earnings.push(earning);
                            }
                            const paycheckPositions = Object.values(positionsBySlug)

                            // Regular earnings render first within each position, then everything
                            // else in its existing order (stable sort).
                            paycheckPositions.forEach((position) => {
                                position.earnings.sort((a, b) =>
                                    (b.earning_type_slug === 'regular' ? 1 : 0) - (a.earning_type_slug === 'regular' ? 1 : 0))
                            })

                            // paycheck.positions.forEach((position, positionIdx) => {
                            paycheckPositions.forEach((position, positionIdx) => {

                                position.earnings.forEach((earning, earningIdx) => {
                                    earning.employee_id = employee.id

                                    const employeeName = {
                                        value: employee.name, //(employee.last_name + ", " + employee.first_name).toUpperCase(),
                                        html: `<a class="uppercase">${employee.name}</a>`,
                                        callback: (val) => {
                                            this.openSidebar(val)
                                        },
                                        callbackValue: employee.id
                                    }
                                    if (paycheck.is_separate_check) {
                                        employeeName.html += ` <i class="ml-1 far fa-fw fa-money-check" style="color: #206067;"></i>`
                                    }

                                    let payRateStr = this.$filters.dollars(earning.rate)
                                    // const payRate = {
                                    //     value: earning.rate,
                                    //     _meta: { field: 'rate' }
                                    // }
                                    const earningHoursOrUnits = {
                                        value: '',
                                        _meta: {}
                                    }
                                    // Each editable cell carries direct refs to the live `earning`
                                    // and `paycheck` it edits, so the commit handler mutates the model
                                    // in place — no id/slug key to reconstruct or re-look-up.
                                    // A zero shows as 0.00 only where someone entered it. Hours, units
                                    // and overtime are stored as typed, so any non-blank value was
                                    // entered; the amount is recalculated on every save, so an entered
                                    // one is flagged with is_amount_entered.
                                    const earningOvertimeHours = {
                                        value: earning.overtime_hours,
                                        _meta: { field: 'overtime_hours', earning: earning, paycheck: paycheck, showZero: this.hasEnteredValue(earning.overtime_hours) }
                                    }
                                    const earningAmount = {
                                        value: earning.amount,
                                        _meta: { field: 'amount', earning: earning, paycheck: paycheck, showZero: Boolean(earning.is_amount_entered) }
                                    }
                                    // Earnings added by hand in Modify Earnings or Separate Check
                                    // get an orange-bordered Amount input
                                    if (earning.is_custom) {
                                        earningAmount._meta.additionalCSSClasses = ['cell-custom-earning']
                                    }
                                    if (earning.earning_type_type == 'hourly') {
                                        // payRate._meta.suffix = " /hr"
                                        payRateStr += " /hr"
                                        earningHoursOrUnits.value = earning.hours
                                        earningHoursOrUnits._meta = { field: 'hours', earning: earning, paycheck: paycheck, showZero: this.hasEnteredValue(earning.hours) }
                                        earningAmount._meta.disabled = true
                                    } else if (earning.earning_type_type == 'per-unit') {
                                        // payRate._meta.suffix = " /unit"
                                        payRateStr += " /unit"
                                        earningHoursOrUnits.value = earning.units
                                        earningHoursOrUnits._meta = { field: 'units', earning: earning, paycheck: paycheck, showZero: this.hasEnteredValue(earning.units) }
                                        earningOvertimeHours._meta.disabled = true
                                        earningAmount._meta.disabled = true
                                    } else if (earning.earning_type_type == 'amount') {
                                        earningHoursOrUnits._meta.disabled = true
                                        earningOvertimeHours._meta.disabled = true
                                    }

                                    let cells
                                    if (this.isSalariedMode) {
                                        cells = [
                                            employeeName,
                                            employee.warning_count,
                                            position.slug,
                                            earning.earning_type_name,
                                            earningHoursOrUnits,
                                            earningAmount,
                                        ]
                                    } else {
                                        cells = [
                                            employeeName,
                                            employee.warning_count,
                                            // payRate,
                                            payRateStr,
                                            position.slug,
                                            earning.earning_type_name,
                                            earningHoursOrUnits,
                                            earningOvertimeHours,
                                            earningAmount,
                                        ]
                                    }

                                    if (!row) {
                                        const employeeId = employee.id
                                        const earningsStatementId = paycheck.earnings_statement_id
                                        row = {
                                            id: earningIdx,
                                            object: earning,
                                            isActive: false,
                                            disableHover: true,
                                            cells: cells,
                                            companyId: company.id,
                                            locationId: location.id,
                                            employeeId: employee.id,
                                            paycheckId: paycheck.id,
                                            // positionName: position.name,
                                            // earningType: earning.earning_type,
                                            subrows: [],
                                            actionsMenuItems: [
                                                {icon: 'far fa-fw fa-pen mr-1', label: 'Modify Earnings', callback: () => this.$emit('open-modify-earnings', [employeeId, earningsStatementId, 'earnings'])},
                                                {icon: 'far fa-fw fa-percent mr-1', label: 'Modify Taxes & Deductions', callback: () => this.$emit('open-modify-earnings', [employeeId, earningsStatementId, 'taxes-deductions'])},
                                                {icon: 'far fa-fw fa-plus mr-1', label: 'Separate Check', callback: () => this.$emit('add-separate-paycheck', [employeeId, false])},
                                                {icon: 'fas fa-fw fa-minus-circle mr-1', label: 'Void/Correct a Previous Check'},
                                                {icon: 'far fa-fw fa-eye mr-1', label: 'Preview Earnings Statement', callback: () => this.$emit('open-earnings-statement', [employeeId, earningsStatementId])},
                                            ],
                                        }
                                    } else {
                                        row.subrows.push(cells)
                                    }
                                })
                            })
                            // All of the paycheck's earnings can belong to the other step,
                            // in which case there is no row for this one.
                            if (row) {
                                sectionData.push(row)
                            }
                        })
                    })
                    // Hide locations whose employees all belong to the other step.
                    if (sectionData.length === 0) {
                        return
                    }
                    companySectionLabels.push(
                        {title: location.name, defaultIsClosed: false, id: location.id, objId: location.id, hasDateFilter: false, status: locationStatus, inputsDisabled: ['ready-for-approval', 'approved', 'completed'].includes(locationStatus)},
                    )
                    companyTableData.push(sectionData)
                })
                // Likewise hide companies with no locations left to show.
                if (companySectionLabels.length === 0) {
                    return
                }
                paygridData.unshift({
                    id: company.id,
                    name: company.name,
                    sectionLabels: companySectionLabels,
                    tableData: companyTableData
                })
            })
            return paygridData
        },
        hasEnteredValue(value) {
            return value !== '' && value != null
        },
        onClick($event) {
            if ($event.target == 'employee-name') {
                this.currentEmployee = $event.employee_id
                this.$emit('click', $event)
            }
        },
        onBlur(msg) {
            this.$emit('blur', msg)
        },
        onSectionReopen(locationId) {
            // Find the location object so the parent can display its name
            for (const company of this.payrollRun.companies) {
                const location = (company.locations || []).find(l => l.id === locationId)
                if (location) {
                    this.$emit('section-reopen', location)
                    return
                }
            }
            // fallback: emit with just the id if location not found
            this.$emit('section-reopen', { id: locationId, name: '' })
        },
        openSidebar(employeeId) {
            this.currentEmployee = employeeId
        },
    },
}
</script>
